import React, { useState } from 'react';
import { ymdLocal } from '../lib/dates';
import api from '../api';

interface Problem {
  document_id: string;
  vendor: string;
  filename: string;
  reason: string;
  // 'missing_account' = mäkký (dá sa obísť force-om), 'no_items' = tvrdý (blokuje vždy)
  type?: 'missing_account' | 'no_items';
  blocking?: boolean;
  missing_items?: Array<{ id: string; description: string; classification: string | null }>;
}

interface ExcludedDoc {
  document_id: string;
  vendor: string;
  filename: string;
  reason: string;
}

interface PreviewResult {
  count: number;
  problems: Problem[];
  // B15: nedaňové doklady (proforma/zálohová) automaticky vylúčené z exportu
  excluded?: ExcludedDoc[];
}

interface Props {
  onClose: () => void;
}

const firstDayOfMonth = (date: Date) => {
  return ymdLocal(new Date(date.getFullYear(), date.getMonth(), 1));
};

const lastDayOfMonth = (date: Date) => {
  return ymdLocal(new Date(date.getFullYear(), date.getMonth() + 1, 0));
};

// Mesačný ZIP balík sa presunul do samostatného modulu Účtovníctvo (/accounting).
type ExportKind = 'eud' | 't01';

const OmegaExportModal: React.FC<Props> = ({ onClose }) => {
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const [kind, setKind] = useState<ExportKind>('eud');
  const [from, setFrom] = useState(firstDayOfMonth(lastMonth));
  const [to, setTo] = useState(lastDayOfMonth(lastMonth));
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewPath = kind === 'eud' ? '/classification/export/eud/preview' : '/classification/export/t01/preview';
  const downloadPath = kind === 'eud' ? '/classification/export/eud' : '/classification/export/t01';
  const filePrefix = kind === 'eud' ? 'omega-prijate-DF-zDF' : 'omega-vystavene-OF';

  const runPreview = async () => {
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await api.get(previewPath, { params: { from, to } });
      setPreview(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Chyba pri náhľade');
    } finally {
      setLoading(false);
    }
  };

  const download = async (force = false) => {
    setDownloading(true);
    setError(null);
    try {
      const res = await api.get(downloadPath, {
        params: force ? { from, to, force: 1 } : { from, to },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filePrefix}-${from}_${to}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      // 422 prichádza ako blob, pokus o parse
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          setError(json.error || 'Chyba pri exporte');
          if (json.problems) {
            setPreview({ count: json.document_count || 0, problems: json.problems, excluded: json.excluded });
          }
        } catch {
          setError('Chyba pri exporte');
        }
      } else {
        setError(e?.response?.data?.error || 'Chyba pri exporte');
      }
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = preview && preview.problems.length === 0 && preview.count > 0;
  // no_items (doklad bez rozpoznaných položiek) sa pri force exporte VYNECHÁ;
  // missing_account (položka bez účtu) ide do TXT s prázdnym účtom.
  const skippedProblems = preview ? preview.problems.filter(p => p.blocking) : [];
  const emptyAccountProblems = preview ? preview.problems.filter(p => !p.blocking) : [];
  const exportableCount = preview ? preview.count - skippedProblems.length : 0;
  // Force ponúkame len pre EUD, keď sú nejaké problémy a aspoň jeden doklad sa dá exportovať.
  const canForce =
    !!preview && kind === 'eud' &&
    preview.problems.length > 0 && exportableCount > 0;

  const forceDownload = async () => {
    const lines = ['Naozaj exportovať teraz?', ''];
    if (emptyAccountProblems.length > 0) {
      lines.push(`• ${emptyAccountProblems.length} doklad(ov) má položky bez účtu — pôjdu do TXT s prázdnym účtom (doplníš v Omege).`);
    }
    if (skippedProblems.length > 0) {
      lines.push(`• ${skippedProblems.length} doklad(ov) s nerozpoznanými položkami sa NEzahrnie — nahodíš ich ručne.`);
    }
    lines.push('', `Vyexportuje sa ${exportableCount} doklad(ov).`);
    if (window.confirm(lines.join('\n'))) download(true);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Export do Omegy</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
          Vygeneruje TXT súbor (Windows-1250, TAB-separated) pre import do KROS Omega.
          Mesačný ZIP balík pre účtovníčku nájdeš v module Účtovníctvo.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {([
            { val: 'eud', label: 'Prijaté faktúry (EUD)', desc: 'Schválené nákladovky, filter podľa dátumu dodania.' },
            { val: 't01', label: 'Vystavené faktúry (Fakturácia)', desc: 'Faktúry z modulu Fakturácia (natívne aj importované), filter podľa dátumu vystavenia.' },
          ] as { val: ExportKind; label: string; desc: string }[]).map(opt => (
            <button
              key={opt.val}
              onClick={() => { setKind(opt.val); setPreview(null); setError(null); }}
              style={{
                flex: 1, padding: '10px 12px', textAlign: 'left',
                border: `2px solid ${kind === opt.val ? '#2563eb' : '#e5e7eb'}`,
                background: kind === opt.val ? '#eff6ff' : '#fff',
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Od (vrátane)</label>
            <input
              type="date"
              value={from}
              onChange={e => { setFrom(e.target.value); setPreview(null); }}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Do (vrátane)</label>
            <input
              type="date"
              value={to}
              onChange={e => { setTo(e.target.value); setPreview(null); }}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={runPreview}
            disabled={loading || !from || !to}
            style={{
              padding: '10px 16px', border: '1px solid #d1d5db',
              borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14,
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'Kontrolujem…' : 'Skontrolovať rozsah'}</button>

          <button
            onClick={() => download()}
            disabled={!canDownload || downloading}
            style={{
              padding: '10px 16px', border: 'none',
              borderRadius: 6, background: canDownload ? '#16a34a' : '#9ca3af',
              color: '#fff', cursor: canDownload ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 500,
              opacity: downloading ? 0.6 : 1,
            }}
          >{downloading ? 'Sťahujem…' : 'Stiahnuť TXT'}</button>

          {canForce && (
            <button
              onClick={forceDownload}
              disabled={downloading}
              title="Exportuje aj doklady s položkami bez účtu — tie budú mať v TXT prázdny účet."
              style={{
                padding: '10px 16px', border: 'none',
                borderRadius: 6, background: '#d97706',
                color: '#fff', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                opacity: downloading ? 0.6 : 1,
              }}
            >{downloading ? 'Sťahujem…' : 'Exportovať aj tak ⚠'}</button>
          )}
        </div>

        {error && (
          <div style={{
            padding: 12, marginBottom: 12,
            background: '#fef2f2', color: '#b91c1c',
            borderRadius: 6, fontSize: 13,
          }}>{error}</div>
        )}

        {preview && (
          <div style={{
            padding: 12, marginBottom: 12,
            background: preview.problems.length === 0 ? '#f0fdf4' : '#fef3c7',
            borderRadius: 6, fontSize: 13,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {preview.count === 0
                ? 'Žiadne schválené doklady v danom rozsahu.'
                : `Nájdených ${preview.count} schválených dokladov.`}
            </div>
            {preview.problems.length > 0 && (
              <>
                <div style={{ marginBottom: 6 }}>
                  <b>{preview.problems.length}</b> doklad(ov) treba doriešiť pred exportom:
                </div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.problems.map(p => (
                    <li key={p.document_id} style={{ marginBottom: 4 }}>
                      <b>{p.vendor || p.filename}</b> — {p.reason}
                      {p.missing_items && p.missing_items.length > 0 && (
                        <ul style={{ margin: '2px 0', paddingLeft: 18, color: '#6b7280' }}>
                          {p.missing_items.slice(0, 3).map(it => (
                            <li key={it.id}>{it.description}</li>
                          ))}
                          {p.missing_items.length > 3 && <li>… a ďalšie</li>}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
                {canForce && (
                  <div style={{ marginTop: 8, color: '#92400e' }}>
                    Cez <b>„Exportovať aj tak ⚠"</b> sa vyexportuje <b>{exportableCount}</b> doklad(ov).
                    {emptyAccountProblems.length > 0 && ' Položky bez účtu pôjdu s prázdnym účtom (doplníš v Omege).'}
                    {skippedProblems.length > 0 && ` ${skippedProblems.length} doklad(ov) s nerozpoznanými položkami sa NEzahrnie — nahodíš ich ručne.`}
                  </div>
                )}
                {exportableCount === 0 && (
                  <div style={{ marginTop: 8, color: '#b91c1c' }}>
                    Žiadny doklad sa nedá exportovať — všetky majú nerozpoznané položky. Treba ich nahodiť ručne.
                  </div>
                )}
              </>
            )}
            {preview.problems.length === 0 && preview.count > 0 && (
              <div style={{ color: '#166534' }}>
                Všetky doklady majú klasifikované položky. Môžeš stiahnuť TXT.
              </div>
            )}
            {kind === 'eud' && preview.excluded && preview.excluded.length > 0 && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e40af' }}>
                <b>{preview.excluded.length}</b> nedaňový(ch) doklad(ov) (proforma/zálohová) sa do exportu nezahrnie:
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {preview.excluded.slice(0, 5).map(e => (
                    <li key={e.document_id}>{e.vendor || e.filename}</li>
                  ))}
                  {preview.excluded.length > 5 && <li>… a ďalšie</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 18px', border: '1px solid #d1d5db',
              borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14,
            }}
          >Zavrieť</button>
        </div>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#374151', textTransform: 'uppercase', letterSpacing: 0.3,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  boxSizing: 'border-box',
};

export default OmegaExportModal;
